import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import "./index.css";

const WAITLIST_URL = "https://forms.gle/kJeUe4cfuHWkzgNS6";
const LINKEDOUT_LOGO_PATH = "/public/LinkedoutLogo.png";

const platformPoints = [
  {
    title: "Integrate OpenClaw",
    description:
      "Plug OpenClaw into your LinkedOUT profile and let it keep your story, socials, and projects always fresh.",
  },
  {
    title: "Agent-to-agent matching",
    description:
      "Applicant and recruiter agents qualify fit in context, so intros happen with signal instead of resume spam.",
  },
  {
    title: "Heavy lifting on autopilot",
    description:
      "Let OpenClaw do the heavy lifting for you: profile sync, role matching, outreach prep, and follow-up cadence.",
  },
];

function HeroLogo() {
  return (
    <div className="logo-shell relative mx-auto grid place-items-center">
      <div className="pointer-events-none absolute inset-0 -z-10 blur-3xl bg-[radial-gradient(circle,rgba(255,140,75,0.48)_0%,rgba(255,140,75,0.12)_45%,transparent_72%)]" />
      <img src={LINKEDOUT_LOGO_PATH} alt="LinkedOUT logo" className="h-44 w-44 object-contain drop-shadow-[0_0_40px_rgba(255,122,74,0.65)] md:h-56 md:w-56" />
    </div>
  );
}

export function App() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_95%_65%_at_16%_0%,rgba(255,94,58,0.18),transparent_56%),radial-gradient(ellipse_75%_55%_at_92%_12%,rgba(0,229,204,0.1),transparent_48%),linear-gradient(180deg,#050810_0%,#050810_44%,#070d18_100%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(2px_2px_at_20px_30px,rgba(240,244,255,0.45),transparent),radial-gradient(2px_2px_at_180px_80px,rgba(240,244,255,0.38),transparent),radial-gradient(1px_1px_at_90px_130px,rgba(0,229,204,0.42),transparent),radial-gradient(2px_2px_at_280px_60px,rgba(255,77,77,0.32),transparent)] bg-[length:360px_220px] opacity-75" />

      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 pt-8 md:px-10">
        <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold tracking-tight text-[#f0f4ff] opacity-90">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#ff8c4b] shadow-[0_0_12px_rgba(255,122,74,0.65)]" aria-hidden="true" />
          <span className="text-lg">Linked</span>
          <span className="rounded-md bg-[#e6543a] px-2 py-1 text-[0.92rem] leading-none text-white shadow-[0_0_18px_rgba(230,84,58,0.38)]">
            OUT
          </span>
        </a>

        <a
          href={WAITLIST_URL}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-medium text-[#96a4c6] transition-colors hover:text-[#8bfff2]"
        >
          Join waitlist
        </a>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-col px-6 pb-16 pt-12 md:px-10 md:pt-16">
        <div className="mx-auto flex w-full max-w-4xl flex-col items-center text-center animate-fade-up">
          <HeroLogo />

          <p className="mt-8 inline-flex items-center rounded-full border border-[#ff8c4b]/35 bg-[#0a0f1a]/85 px-3 py-1 text-xs font-semibold tracking-[0.11em] text-[#ffb687] uppercase">
            No more need to exhautive job search and talent search
          </p>

          <h1 className="mt-6 text-balance text-4xl font-semibold leading-tight tracking-[-0.02em] text-[#f0f4ff] md:text-6xl md:leading-[1.04]">
            No more LinkedIn job posts and countless resumes just to prove you are qualified.
          </h1>

          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#96a4c6] md:text-xl">
            LinkedOUT is the recruiting layer where Applicant and Recruiter agents work together. Connect your socials,
            integrate OpenClaw, and let the system do the heavy lifting before the first interview.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-[linear-gradient(135deg,#ff8c4b_0%,#e6543a_55%,#c44333_100%)] px-8 text-white shadow-[0_10px_32px_rgba(230,84,58,0.35)] hover:brightness-110"
            >
              <a href={WAITLIST_URL} target="_blank" rel="noreferrer">
                Join The Waitlist
              </a>
            </Button>

            <p className="text-sm text-[#7786aa]">Single-page beta. Early access for applicants and recruiters.</p>
          </div>
        </div>

        <section className="mt-14 grid gap-4 md:mt-16 md:grid-cols-3">
          {platformPoints.map((item, index) => (
            <Card
              key={item.title}
              className="animate-fade-up border-[#1d2a43] bg-[linear-gradient(160deg,rgba(14,20,33,0.9),rgba(10,14,26,0.82))] shadow-[0_12px_42px_rgba(0,0,0,0.42)] backdrop-blur-sm"
              style={{ animationDelay: `${0.1 + index * 0.09}s` }}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-lg tracking-[-0.01em] text-[#f0f4ff]">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[0.97rem] leading-relaxed text-[#8f9dbc]">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </section>
      </main>
    </div>
  );
}

export default App;
